import { GamepadRef, useGamepads, useUserTenantPreference } from 'kineticUtils';
import React, { useCallback, useRef, useState } from 'react';
import { PREFERENCE_KEYS } from 'types';

const TICK_RATE = 50;

export interface JoystickContextState {
  hasJoystick: boolean;
  currentAction?: JoystickAction;
  axisState: AxisState;
}

export interface AxisState {
  axis: number[];
  idle: boolean;
}

export enum VideoHotkeyAction {
  NEXT_CAMERA = 'NEXT_CAMERA',
  PREVIOUS_CAMERA = 'PREVIOUS_CAMERA',
  LAST_CAMERA = 'LAST_CAMERA',
  GO_TO_PRESET = 'GO_TO_PRESET',
  GO_TO_CAMERA = 'GO_TO_CAMERA',
  CHANGE_LAYOUT = 'CHANGE_LAYOUT',
  CONFIRM = 'CONFIRM',
  CANCEL = 'CANCEL',
  KEYBOARD_INPUT = 'KEYBOARD_INPUT',
}

// add other actions as needed
export const HotkeyActions = {
  ...VideoHotkeyAction,
};

export interface JoystickAction {
  type: keyof typeof HotkeyActions;
  payload?: any;
}

export const HotKeyRequiresPayload: { [key in keyof typeof HotkeyActions]?: boolean } = {
  [VideoHotkeyAction.GO_TO_PRESET]: true,
  [VideoHotkeyAction.KEYBOARD_INPUT]: true,
};

export const JoystickContext = React.createContext<JoystickContextState>({
  hasJoystick: false,
  currentAction: undefined,
  axisState: {
    axis: [],
    idle: true,
  },
});

const roundToTwoDecimals = (value: number) => {
  return Math.round(value * 100) / 100;
};

interface ButtonMappingsRef {
  [key: number]: boolean;
}

interface ButtonPressRef {
  [key: string]: ButtonMappingsRef;
}

export const useJoystickContext = (tickRate?: number): JoystickContextState => {
  const [currentAction, setCurrentAction] = useState<JoystickAction>();
  const [hasJoystick, setHasJoystick] = useState<boolean>(false);
  const buttonPressMap = useRef<ButtonPressRef>({});
  const { value: preferenceLayouts } = useUserTenantPreference<JoystickLayoutPreference>({
    key: PREFERENCE_KEYS.JOYSTICK_HOTKEYS,
  });
  const { value: mappings } = useUserTenantPreference<GamepadToLayoutMapping>({
    key: PREFERENCE_KEYS.JOYSTICK_HOTKEYS_MAPPING,
  });
  const [selectedAxisJoystick, setSelectedAxisJoystick] = useState<number | undefined>(undefined);
  const [axisState, setAxisState] = useState<AxisState>({
    axis: [],
    idle: true,
  });
  // used to track the last timestamp of the gamepad update
  const lastTimestampRef = useRef<number>(0);
  // used to track how many ticks the joystick has been idle
  const idleTimes = useRef<number>(0);

  const gamepadUpdated = useCallback(
    (gamepadRef: GamepadRef) => {
      if (!gamepadRef) {
        setHasJoystick(false);
        setSelectedAxisJoystick(undefined);
        setAxisState({ axis: [], idle: true });
        lastTimestampRef.current = 0;
        return;
      }
      if (hasJoystick && Object.values(gamepadRef).length === 0) {
        setHasJoystick(false);
      }
      if (!hasJoystick && Object.values(gamepadRef).length > 0) {
        setHasJoystick(true);
      }
      if (currentAction) {
        setCurrentAction(undefined);
      }
      // check all gamepads for button pushes
      Object.values(gamepadRef).forEach((gamepadButton: Gamepad) => {
        gamepadButton.buttons.forEach((button, index) => {
          const gamepadButtonId = `${gamepadButton.id}-${index}`;
          if (!buttonPressMap.current[gamepadButtonId]) {
            buttonPressMap.current[gamepadButtonId] = {};
          }
          if (buttonPressMap.current[gamepadButtonId][index] && !button.pressed) {
            // button has been released
            const gamepadLayoutId = mappings[gamepadButton.id];
            const foundLayout =
              (preferenceLayouts?.layouts || DEFAULT_JOYSTICK_LAYOUTS).find(
                (layout) => layout.id === gamepadLayoutId
              ) ?? DEFAULT_JOYSTICK_LAYOUT;
            setCurrentAction(foundLayout[index]);
          }
          buttonPressMap.current[gamepadButtonId][index] = button.pressed;
        });
      });

      const axisGamepad =
        selectedAxisJoystick !== undefined ? gamepadRef[selectedAxisJoystick] : undefined;
      if (selectedAxisJoystick === undefined || !axisGamepad || !axisGamepad.axes?.length) {
        for (const key in gamepadRef) {
          const gamepad = gamepadRef[key];
          if (gamepad.axes.length > 0) {
            setSelectedAxisJoystick(Number(key));
            lastTimestampRef.current = 0;
            break;
          }
        }
      }
      if (selectedAxisJoystick === undefined || !axisGamepad) {
        return;
      }
      const timeDifference = axisGamepad.timestamp - lastTimestampRef.current;
      lastTimestampRef.current = axisGamepad.timestamp;
      if (timeDifference === 0) {
        idleTimes.current++;
        // determine 5 seconds based on the TICK_RATE
        const idleTicks = (1000 / TICK_RATE) * 5;
        // set idle to true if the joystick has been idle for 5 seconds
        if (idleTimes.current === idleTicks) {
          setAxisState({
            axis: [0, 0, 0],
            idle: true,
          });
          setSelectedAxisJoystick(undefined);
        }
        return;
      }
      idleTimes.current = 0;
      const rawAxes = axisGamepad.axes.map(roundToTwoDecimals);
      const DEAD_ZONE = 0.1;
      const filteredAxes = rawAxes.map((v) => (Math.abs(v) < DEAD_ZONE ? 0 : v));
      setAxisState({ axis: [filteredAxes[0], -1 * filteredAxes[1], filteredAxes[2]], idle: false });
    },
    [currentAction, hasJoystick, mappings, preferenceLayouts?.layouts, selectedAxisJoystick]
  );
  useGamepads(gamepadUpdated, tickRate ? tickRate : TICK_RATE);
  return {
    hasJoystick,
    currentAction: currentAction,
    axisState,
  };
};

export interface JoystickLayoutPreference {
  layouts: JoyStickHotKeyLayout[];
}

export interface JoyStickHotKeyLayout {
  name: string;
  id: string;
  [key: number]: JoystickAction;
}

export interface GamepadToLayoutMapping {
  [key: string]: string;
}

export const AXIS_TU9002_JoystickLayout: JoyStickHotKeyLayout = {
  name: 'AXIS TU9002 Joystick',
  id: 'AXIS TU9002 Joystick (Vendor: 2225 Product: 3215)',
  0: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 1,
  },
  1: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 2,
  },
  2: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 3,
  },
  3: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 4,
  },
  4: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 5,
  },
  5: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 6,
  },

  8: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 7,
  },
  9: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 8,
  },
  10: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 9,
  },
  11: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 10,
  },
  6: {
    type: VideoHotkeyAction.PREVIOUS_CAMERA,
  },
  7: {
    type: VideoHotkeyAction.NEXT_CAMERA,
  },
  14: {
    type: VideoHotkeyAction.PREVIOUS_CAMERA,
  },
  15: {
    type: VideoHotkeyAction.NEXT_CAMERA,
  },
};

export const AXIS_TU9003_KeypadLayout: JoyStickHotKeyLayout = {
  name: 'AXIS TU9003 Keypad',
  id: 'AXIS TU9003 Keypad (Vendor: 2225 Product: 3216)',
  0: {
    type: VideoHotkeyAction.CONFIRM,
  },
  1: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 0,
  },
  2: {
    type: VideoHotkeyAction.LAST_CAMERA,
  },
  3: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 1,
  },
  4: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 2,
  },
  5: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 3,
  },
  6: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 4,
  },
  7: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 5,
  },
  8: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 6,
  },
  9: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 7,
  },
  10: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 8,
  },
  11: {
    type: VideoHotkeyAction.KEYBOARD_INPUT,
    payload: 9,
  },
  12: {
    type: VideoHotkeyAction.CHANGE_LAYOUT,
  },
  13: {
    type: VideoHotkeyAction.GO_TO_CAMERA,
  },
  14: {
    type: VideoHotkeyAction.NEXT_CAMERA,
  },
  15: {
    type: VideoHotkeyAction.PREVIOUS_CAMERA,
  },
  16: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 1,
  },
  17: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 2,
  },
  18: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 3,
  },
  19: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 4,
  },
  20: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 5,
  },
  21: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 6,
  },
  22: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 7,
  },
  23: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 8,
  },
  24: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 9,
  },
  25: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 10,
  },
};

export const DEFAULT_JOYSTICK_LAYOUT: JoyStickHotKeyLayout = {
  name: 'Default Joystick',
  id: 'DEFAULT_JOYSTICK_LAYOUT',
  0: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 1,
  },
  1: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 2,
  },
  2: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 3,
  },
  3: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 4,
  },

  4: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 5,
  },
  5: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 6,
  },
  6: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 7,
  },
  7: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 8,
  },
  8: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 9,
  },
  9: {
    type: VideoHotkeyAction.GO_TO_PRESET,
    payload: 10,
  },
  10: {
    type: VideoHotkeyAction.PREVIOUS_CAMERA,
  },
  11: {
    type: VideoHotkeyAction.NEXT_CAMERA,
  },
};

export const DEFAULT_JOYSTICK_LAYOUTS: JoyStickHotKeyLayout[] = [
  DEFAULT_JOYSTICK_LAYOUT,
  AXIS_TU9002_JoystickLayout,
  AXIS_TU9003_KeypadLayout,
];
